/**
 * Invertible instrument / bank patches (Phase 5).
 */
import type {
  Instrument,
  InstrumentSlot,
  SamplerPreset,
  SynthPreset,
  WavetablePreset,
} from "../../../../src/lib/audio";
import type { NamedDrumKit } from "../lib/drumKitBank";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";
import type {
  SynthBankSnapshot,
  WavetableBankSnapshot,
  SamplerBankSnapshot,
} from "./instrumentBridge";

function requireInstrument(context: AdamCommandContext) {
  const instruments = context.instruments;
  if (!instruments) {
    throw new Error("Adam instrument bridge is not registered");
  }
  return instruments;
}

function cloneInstrumentValue(instrument: Instrument): Instrument {
  return structuredClone(instrument);
}

function cloneSynthSnapshot(snapshot: SynthBankSnapshot): SynthBankSnapshot {
  return structuredClone(snapshot);
}

function cloneWtSnapshot(snapshot: WavetableBankSnapshot): WavetableBankSnapshot {
  return structuredClone(snapshot);
}

function synthTopologyKey(preset: SynthPreset): string {
  return [
    preset.name,
    (preset.routes ?? []).map(r => `${r.source}->${r.target}`).join(","),
  ].join("|");
}

function wavetableTopologyKey(preset: WavetablePreset): string {
  return [
    preset.name,
    preset.tableId ?? "",
    (preset.routes ?? []).map(r => `${r.source}->${r.target}`).join(","),
  ].join("|");
}

function samplerTopologyKey(preset: SamplerPreset): string {
  return [
    preset.name,
    preset.primarySampleId ?? "",
    preset.playMode,
    preset.reverse ? "1" : "0",
    preset.loop.enabled ? "1" : "0",
    preset.loop.mode ?? "",
    (preset.zones ?? [])
      .map(z =>
        [
          z.id,
          z.sampleId,
          z.keyLow,
          z.keyHigh,
          z.rootKey,
          z.padIndex ?? "",
        ].join(":"),
      )
      .join(","),
    (preset.routes ?? []).map(r => `${r.source}->${r.target}`).join(","),
    (preset.slices ?? []).map(s => `${s.id}${s.omit ? ":omit" : ""}`).join(","),
  ].join("|");
}

/**
 * Structural identity for scrub-merge. Param knobs / region-loop braces may
 * merge; sample load, zone map, mod-route add/remove, etc. start a new undo step.
 */
export function instrumentTopologyKey(instrument: Instrument): string {
  switch (instrument.kind) {
    case "synth":
      return `synth|${synthTopologyKey(instrument.preset)}`;
    case "wavetable":
      return `wavetable|${wavetableTopologyKey(instrument.preset)}`;
    case "sampler":
      return `sampler|${samplerTopologyKey(instrument.preset)}`;
    case "drums":
      return `drums|${instrument.kit.name}|${instrument.kit.pads.map((p, i) => `${i}:${p.name}:${p.level}`).join(",")}`;
    case "sf2":
      return `sf2|${instrument.patch.fontId}|${instrument.patch.bank}:${instrument.patch.program}`;
    case "externalMidi":
      return `externalMidi|${instrument.patch.outputDeviceId}|ch${instrument.patch.channel}`;
    default: {
      const _exhaustive: never = instrument;
      return String(_exhaustive);
    }
  }
}

function isParamOnlyInstrumentEdit(from: Instrument, to: Instrument): boolean {
  return from.kind === to.kind && instrumentTopologyKey(from) === instrumentTopologyKey(to);
}

/** Sorted leaf paths that differ — used so only same-knob scrub coalesces. */
export function instrumentChangedPaths(from: Instrument, to: Instrument): string[] {
  const paths: string[] = [];
  collectChangedPaths(from as unknown, to as unknown, "", paths);
  paths.sort();
  return paths;
}

function collectChangedPaths(a: unknown, b: unknown, prefix: string, out: string[]): void {
  if (Object.is(a, b)) {
    return;
  }
  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr || bArr) {
    if (!aArr || !bArr || a.length !== b.length) {
      out.push(prefix || ".");
      return;
    }
    for (let i = 0; i < a.length; i++) {
      collectChangedPaths(a[i], b[i], prefix ? `${prefix}.${i}` : String(i), out);
    }
    return;
  }
  const aObj = a !== null && typeof a === "object";
  const bObj = b !== null && typeof b === "object";
  if (aObj && bObj) {
    const aRec = a as Record<string, unknown>;
    const bRec = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(aRec), ...Object.keys(bRec)]);
    for (const key of keys) {
      collectChangedPaths(
        aRec[key],
        bRec[key],
        prefix ? `${prefix}.${key}` : key,
        out,
      );
    }
    return;
  }
  out.push(prefix || ".");
}

function sameChangedPaths(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0 || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Replace one slot's live instrument.
 * Continuous scrub of the *same* leaf path(s) merges; switching knobs / topology does not.
 */
export class SetInstrumentCommand implements AdamCommand {
  readonly id = "setInstrument";
  readonly label = "Instrument";

  constructor(
    readonly slot: InstrumentSlot,
    readonly from: Instrument,
    readonly to: Instrument,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).setInstrument(this.slot, cloneInstrumentValue(this.to));
    return { changed: "instrument" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).setInstrument(this.slot, cloneInstrumentValue(this.from));
    return { changed: "instrument" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    if (!(command instanceof SetInstrumentCommand) || command.slot !== this.slot) {
      return false;
    }
    if (!isParamOnlyInstrumentEdit(this.from, this.to)) {
      return false;
    }
    if (!isParamOnlyInstrumentEdit(command.from, command.to)) {
      return false;
    }
    if (instrumentTopologyKey(this.to) !== instrumentTopologyKey(command.from)) {
      return false;
    }
    return sameChangedPaths(
      instrumentChangedPaths(this.from, this.to),
      instrumentChangedPaths(command.from, command.to),
    );
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetInstrumentCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetInstrumentCommand(
      this.slot,
      cloneInstrumentValue(this.from),
      cloneInstrumentValue(command.to),
    );
  }
}

/** Apply a synth bank + instruments snapshot (apply / save / rename / delete / import). */
export class SetSynthBankCommand implements AdamCommand {
  readonly id = "setSynthBank";
  readonly label: string;

  constructor(
    readonly from: SynthBankSnapshot,
    readonly to: SynthBankSnapshot,
    readonly changedSlots: InstrumentSlot[],
    options?: { label?: string },
  ) {
    this.label = options?.label ?? "Preset bank";
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applySynthBankSnapshot(
      cloneSynthSnapshot(this.to),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applySynthBankSnapshot(
      cloneSynthSnapshot(this.from),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Apply a wavetable bank + instruments snapshot. */
export class SetWavetableBankCommand implements AdamCommand {
  readonly id = "setWavetableBank";
  readonly label: string;

  constructor(
    readonly from: WavetableBankSnapshot,
    readonly to: WavetableBankSnapshot,
    readonly changedSlots: InstrumentSlot[],
    options?: { label?: string },
  ) {
    this.label = options?.label ?? "Wavetable bank";
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applyWavetableBankSnapshot(
      cloneWtSnapshot(this.to),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applyWavetableBankSnapshot(
      cloneWtSnapshot(this.from),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Apply a sampler bank + instruments snapshot. */
export class SetSamplerBankCommand implements AdamCommand {
  readonly id = "setSamplerBank";
  readonly label: string;

  constructor(
    readonly from: SamplerBankSnapshot,
    readonly to: SamplerBankSnapshot,
    readonly changedSlots: InstrumentSlot[],
    options?: { label?: string },
  ) {
    this.label = options?.label ?? "Sampler bank";
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applySamplerBankSnapshot(
      structuredClone(this.to),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).applySamplerBankSnapshot(
      structuredClone(this.from),
      this.changedSlots,
    );
    return { changed: "bank" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Replace the drum-kit bank list (save/as). */
export class SetDrumBankCommand implements AdamCommand {
  readonly id = "setDrumBank";
  readonly label = "Drum bank";

  constructor(
    readonly from: NamedDrumKit[],
    readonly to: NamedDrumKit[],
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).setDrumBank(structuredClone(this.to));
    return { changed: "bank" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireInstrument(context).setDrumBank(structuredClone(this.from));
    return { changed: "bank" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}
